import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { SessionsAndEventsResponse } from "../../../../api/services/program/models";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiSelect,
} from "../../../../components";
import {
  TimelineActivityDataItem,
  TimelineActivityType,
} from "../../../../components/timelineActivity/TimelineActivity";
import { TimelineActivityTable } from "../../../../components/timelineActivityTable/TimelineActivityTable";
import {
  EMPTY_OBJECT,
  getCustomDateFormatWithTime,
  labelKey,
  PAGE_IDS,
} from "../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../helpers/constants/countryDateFormat";
import { LabelValue } from "../../../../helpers/types/common";
import {
  convertToUserFormat,
  convertToUserFormatWithLocalTime,
  getFullName,
  getValueByKey,
  getValueOrBlank,
  isEmpty,
  isNotEmpty,
} from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useMediaQueries } from "../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../redux/slices/appState/appStateSelector";
import { getFormattedDuration, isWithinPeriod } from "../../helper";
import {
  CATEGORY_FILTER_KEYS,
  ManageParticipantsSessionAndEventsFilterFormKeys,
  PAST_PERIOD_FILTER_KEYS,
  TYPE_FILTER_KEYS,
  UPCOMING_PERIOD_FILTER_KEYS,
} from "../../modelsAndSchemas/Models";
import { manageParticipantsSessionAndEventsSiteCoreKeys } from "../../sitecore/SiteCoreKeys";
import { ManageParticipantsSessionAndEventsTabWrapper } from "./ManageParticipantsSessionAndEventsTab.style";

interface ManageParticipantsSessionAndEventsTabProps {
  sessionsEventList?: SessionsAndEventsResponse[];
  showEventCategory?: boolean;
}

const pageId = PAGE_IDS.COACH_PORTAL_VIEW_PARTICIPANT_SESSION_EVENTS;
export const ManageParticipantsSessionAndEventsTab = ({
  sessionsEventList,
  showEventCategory = true,
}: ManageParticipantsSessionAndEventsTabProps) => {
  const { commonARIALabel } = useNavigationSiteCore();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );
  const {
    useFetchLabel,
    commonLabels,
    commonDropdownValues,
    fetchValueByKeyOfDropdown,
    fetchDropdownValues,
  } = useSiteCore();
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();

  const [filteredSessionsAndEventsData, setFilteredSessionsAndEventsData] =
    useState<TimelineActivityType[]>([]);
  const [sessionAndEventsData, setSessionAndEventsData] = useState<
    TimelineActivityType[]
  >([]);
  const tableRef = useRef<{ resetPagination: () => void }>(null);

  const { control, getValues, setValue } = useFormContext();
  const formValues = useWatch();
  const { type, category } = getValues();
  const { isMobile, isTablet } = useMediaQueries();

  const isCoachingAndSessionCategory = useMemo(
    () => category === CATEGORY_FILTER_KEYS.COACHING_SESSIONS,
    [category],
  );

  const {
    sessionAndEventsTitle,
    typeDropDown,
    periodDropDown,
    categoryDropDown,
    title: titleLabelKey,
    createdDate: createdDateLabelKey,
    meetingDate: meetingDateLabelKey,
    eventDate: eventDateLabelKey,
    meetingDuration: meetingDurationLabelKey,
    eventDuration: eventDurationLabelKey,
    category: categoryLabelKey,
    mode: modeLabelKey,
    initiatedBy: initiatedByLabelKey,
    instructor: instructorLabelKey,
    meetingUpdate: meetingUpdateLabelKey,
    eventStatus: eventStatusLabelKey,
    noCoachingSessionsAvailable,
    noEventsAvailable,
    pastSessionsAndEventsPeriodList,
    sessionsAndEventsCategoryList,
    sessionsAndEventsTypeList,
    upcomingSessionsAndEventsPeriodList,
    meetingTypeListKey,
  } = manageParticipantsSessionAndEventsSiteCoreKeys;

  const getLabel = useFetchLabel(sessionAndEventsTitle);
  const getDropdownValues = (key: string) => {
    return fetchDropdownValues(sessionAndEventsTitle, key);
  };

  const [periodListKey, setPeriodListKey] = useState(
    upcomingSessionsAndEventsPeriodList,
  );

  const transformSessionData = (
    session: SessionsAndEventsResponse,
  ): TimelineActivityDataItem[] => {
    const {
      title,
      createdDate,
      durationInSeconds,
      endDateTime,
      initiatedBy,
      startDateTime,
      category,
      eventDate,
      eventStatus,
      instructor,
    } = session;

    const dummyMeetingUpdate = "";
    const eventStatusValue = getValueByKey(
      commonDropdownValues.statusList,
      eventStatus,
    );

    const getCoachingAndSessionLabels = () => {
      return [
        {
          key: titleLabelKey,
          value: title,
        },
        ...(!isMobile && !isTablet ? [EMPTY_OBJECT, EMPTY_OBJECT] : []),
        {
          key: createdDateLabelKey,
          value: convertToUserFormat(createdDate, userCountryDateFormat),
        },
        {
          key: meetingDateLabelKey,
          value: convertToUserFormatWithLocalTime(
            startDateTime,
            userCountryDateFormat,
          ),
        },
        {
          key: meetingDurationLabelKey,
          value: getFormattedDuration(
            startDateTime,
            endDateTime,
            durationInSeconds,
            loggedInUser?.timeZone,
          ),
        },
        {
          key: categoryLabelKey,
          value: category,
        },
        {
          key: modeLabelKey,
          value: commonLabels.online,
        },
        {
          key: initiatedByLabelKey,
          value: getValueOrBlank(
            getFullName(initiatedBy?.firstName, initiatedBy?.lastName, true),
          ),
        },
        ...(isNotEmpty(dummyMeetingUpdate)
          ? [
              {
                key: meetingUpdateLabelKey,
                value: dummyMeetingUpdate,
              },
            ]
          : []),
      ];
    };

    const getLiveWebinarEventLabels = () => {
      return [
        {
          key: titleLabelKey,
          value: fetchMeetingTitle(title),
        },
        ...(!isMobile && !isTablet ? [EMPTY_OBJECT, EMPTY_OBJECT] : []),
        {
          key: eventDateLabelKey,
          value: convertToUserFormatWithLocalTime(
            startDateTime,
            userCountryDateFormat,
          ),
        },
        {
          key: eventDurationLabelKey,
          value: getFormattedDuration(
            startDateTime,
            endDateTime,
            durationInSeconds,
            loggedInUser?.timeZone,
          ),
        },
        {
          key: categoryLabelKey,
          value: category,
        },
        {
          key: modeLabelKey,
          value: commonLabels.online,
        },
        {
          key: instructorLabelKey,
          value: getValueOrBlank(instructor),
        },
        {
          key: eventStatusLabelKey,
          value: getValueOrBlank(eventStatusValue),
        },
      ];
    };

    const labels: {
      key: string;
      value: string | LabelValue[];
      listKey?: string;
      replaceEmptyWithDash?: boolean;
    }[] = isCoachingAndSessionCategory
      ? getCoachingAndSessionLabels()
      : getLiveWebinarEventLabels();

    return labels
      .filter(Boolean)
      .map(({ key, value, listKey, replaceEmptyWithDash }) => {
        return {
          type: labelKey,
          label: getLabel(key),
          value: listKey
            ? fetchValueByKeyOfDropdown(
                sessionAndEventsTitle,
                listKey,
                getValueOrBlank(value as string),
              )
            : value,
          replaceEmptyWithDash: replaceEmptyWithDash,
        };
      });
  };

  const fetchMeetingTitle = (title: string) => {
    const titleValue = fetchValueByKeyOfDropdown(
      sessionAndEventsTitle,
      meetingTypeListKey,
      title,
    );
    if (isNotEmpty(titleValue)) return titleValue;
    return title;
  };

  useEffect(() => {
    if (
      !siteCoreLoading &&
      sessionsEventList &&
      isNotEmpty(sessionsEventList)
    ) {
      const localData = sessionsEventList.map(
        (session: SessionsAndEventsResponse) => ({
          timeStamp: convertToUserFormatWithLocalTime(
            isCoachingAndSessionCategory
              ? session.startDateTime
              : session.eventDate,
            getCustomDateFormatWithTime(userCountryDateFormat),
            loggedInUser?.timeZone,
          ),
          startDateTime: session.startDateTime,
          endDateTime: session.endDateTime,
          id: session.id,
          title: fetchMeetingTitle(session.title),
          creator: getValueOrBlank(
            getFullName(
              session?.conductedWith?.firstName,
              session?.conductedWith?.lastName,
              true,
            ),
          ),
          instructor: getValueOrBlank(session?.instructor),
          isCollapsible: isNotEmpty(session),
          expandableData: transformSessionData(session),
          showMoreOptions: false,
          isNotClosureType: true,
          category: getValueOrBlank(session?.type),
        }),
      );

      setSessionAndEventsData(localData);
    } else {
      setSessionAndEventsData([]);
    }
  }, [sessionsEventList, siteCoreLoading, isCoachingAndSessionCategory]);

  useEffect(() => {
    applyFilters();
  }, [sessionsEventList, sessionAndEventsData, formValues]);

  useEffect(() => {
    handleTypeDropdownChange(type);
  }, [type]);

  const applyFilters = () => {
    resetPagination();
    const { type, period, category } = getValues();

    const now = dayjs().utc();
    const filteredData = sessionAndEventsData
      .filter((item) => {
        const targetDate = dayjs(item.startDateTime).utc();
        const matchesType =
          type === TYPE_FILTER_KEYS.UPCOMING_SESSIONS_AND_EVENTS
            ? targetDate.isAfter(now)
            : targetDate.isBefore(now);
        const matchesPeriod = isWithinPeriod(
          getValueOrBlank(item.startDateTime),
          period,
          type,
        );
        const matchesCategory =
          item.category === category || category === CATEGORY_FILTER_KEYS.ALL;
        return matchesType && matchesPeriod && matchesCategory;
      })
      .sort((a, b) => {
        const dateA = dayjs(a.startDateTime).utc();
        const dateB = dayjs(b.startDateTime).utc();
        return dateB.valueOf() - dateA.valueOf();
      });
    setFilteredSessionsAndEventsData(filteredData);
  };

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  const handleTypeDropdownChange = (value: string) => {
    const isPastSessionAndEvents =
      value === TYPE_FILTER_KEYS.PAST_SESSIONS_AND_EVENTS;
    setPeriodListKey(
      isPastSessionAndEvents
        ? pastSessionsAndEventsPeriodList
        : upcomingSessionsAndEventsPeriodList,
    );
    setValue(
      ManageParticipantsSessionAndEventsFilterFormKeys.period,
      isPastSessionAndEvents
        ? PAST_PERIOD_FILTER_KEYS.PAST_7_DAYS
        : UPCOMING_PERIOD_FILTER_KEYS.NEXT_7_DAYS,
    );
    applyFilters();
  };

  return (
    <ManageParticipantsSessionAndEventsTabWrapper>
      <MuiBox
        className="session-events-timeline"
        data-testid="participant-session-events-timeline"
      >
        <MuiGrid
          container
          className="filter-wrapper"
          data-testid="session-events-filter-wrapper"
        >
          <MuiGrid
            size={gridSize}
            className={gridSizeMediumClass}
            spacing={isMobile ? 2 : 0}
          >
            <FormController
              control={control}
              name={ManageParticipantsSessionAndEventsFilterFormKeys.type}
            >
              <MuiSelect
                label={getLabel(typeDropDown)}
                options={getDropdownValues(sessionsAndEventsTypeList)}
                onChange={(e) =>
                  handleTypeDropdownChange(e.target.value as string)
                }
                id={`${pageId}-${ManageParticipantsSessionAndEventsFilterFormKeys.type}`}
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid
            size={gridSize}
            className={gridSizeMediumClass}
            spacing={isMobile ? 2 : 0}
          >
            <FormController
              control={control}
              name={ManageParticipantsSessionAndEventsFilterFormKeys.period}
            >
              <MuiSelect
                label={getLabel(periodDropDown)}
                options={getDropdownValues(periodListKey)}
                id={`${pageId}-${ManageParticipantsSessionAndEventsFilterFormKeys.period}`}
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>

          {showEventCategory && (
            <MuiGrid
              size={gridSize}
              className={gridSizeMediumClass}
              spacing={isMobile ? 2 : 0}
            >
              <FormController
                control={control}
                name={ManageParticipantsSessionAndEventsFilterFormKeys.category}
              >
                <MuiSelect
                  label={getLabel(categoryDropDown)}
                  options={getDropdownValues(sessionsAndEventsCategoryList)}
                  id={`${pageId}-${ManageParticipantsSessionAndEventsFilterFormKeys.category}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
          )}
        </MuiGrid>

        <TimelineActivityTable
          ref={tableRef}
          activityData={filteredSessionsAndEventsData}
          displayWith={isCoachingAndSessionCategory}
          pageId={pageId}
          noDataInApi={isEmpty(filteredSessionsAndEventsData)}
          emptyMessage={getLabel(
            isCoachingAndSessionCategory
              ? noCoachingSessionsAvailable
              : noEventsAvailable,
          )}
          data-testid="session-events-table"
        />
      </MuiBox>
    </ManageParticipantsSessionAndEventsTabWrapper>
  );
};
